import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: '온니잡',
        description: '룸살롱, 노래방, 호빠 등 야간 업종 특화 구인구직 플랫폼',
        inLanguage: 'ko-KR',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: '온니잡',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`,
        },
        sameAs: ['https://onnyjob.kr'],
        description: '퀸알바의 선택! 야간 업종 특화 구인구직 플랫폼 온니잡',
        keywords: '초톡, 강남초톡, 강남키티, 강키, 룸빵닷컴, 온니잡, 퀸알바, 룸살롱 구인구직, 노래방 알바, 호빠 채용',
      },
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/#webpage`,
        url: siteUrl,
        name: '온니잡 | 퀸알바 구인구직 플랫폼 — 룸살롱·노래방·호빠 채용',
        description:
          '온니잡은 룸살롱, 노래방, 호빠 등 야간 업종 특화 구인구직 플랫폼입니다. 50만+ 누적 구직자, 3만+ 파트너 업체, 92% 취업 성공률.',
        inLanguage: 'ko-KR',
        isPartOf: { '@id': `${siteUrl}/#website` },
        about: { '@id': `${siteUrl}/#organization` },
        dateModified: '2025-07-15',
        keywords: '초톡, 강남초톡, 강남키티, 강키, 룸빵닷컴, 온니잡, 퀸알바, 룸살롱 구인구직, 노래방 알바, 호빠 채용',
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: '온니잡은 무료로 이용할 수 있나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '네, 온니잡은 구직자 기준 완전 무료입니다. 호빠·호스트바 업체의 구인 광고도 무료로 등록하실 수 있습니다.',
            },
          },
          {
            '@type': 'Question',
            name: '어떤 업종의 구인구직이 가능한가요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '룸살롱, 클럽, 노래방, 단란주점, 호빠, 호스트바 등 야간 업종 전반의 구인구직이 가능합니다.',
            },
          },
          {
            '@type': 'Question',
            name: '업체와 어떻게 연락하나요?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '마음에 드는 공고에 원클릭 지원 후 업체와 바로 채팅으로 연결됩니다. 번거로운 전화 없이 빠르게 확인하실 수 있습니다.',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
      />
      <div className="min-h-screen bg-[#1a0b2e] text-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#1a0b2e] shadow-lg' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="text-2xl font-bold">온니잡</div>
            <div className="flex items-center gap-6">
              <a href="#features" className="text-sm hover:text-yellow-400 transition-colors whitespace-nowrap">서비스 소개</a>
              <a href="#reviews" className="text-sm hover:text-yellow-400 transition-colors whitespace-nowrap">후기</a>
              <a href="#industry" className="text-sm hover:text-yellow-400 transition-colors whitespace-nowrap">업종별 광고</a>
              <a href="https://onnyjob.kr" target="_blank" rel="noopener noreferrer" className="bg-yellow-400 text-black px-6 py-2 rounded-full text-sm font-semibold hover:bg-yellow-300 transition-colors whitespace-nowrap cursor-pointer">
                시작하기
              </a>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-[#1a0b2e] to-[#1a0b2e]"></div>
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  opacity: Math.random() * 0.7 + 0.3
                }}
              ></div>
            ))}
          </div>

          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-6 py-2 mb-8">
              <span className="text-2xl">👑</span>
              <span className="text-sm font-medium">퀸알바의 선택</span>
            </div>

            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              <div className="mb-2">퀸알바의 선택은?</div>
              <div className="text-yellow-400">온니잡에서!</div>
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-4">
              밤을 빛내는 당신을 위한 구인구직 플랫폼 👑
            </p>
            <p className="text-base text-gray-400 mb-12">
              룸살롱, 노래방, 호빠까지 — 내게 맞는 자리를 온니잡에서 찾으세요.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="https://onnyjob.kr" target="_blank" rel="noopener noreferrer" className="bg-yellow-400 text-black px-8 py-4 rounded-full text-base font-bold hover:bg-yellow-300 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
                👑 지금 바로 시작하기
              </a>
              <a href="#features" className="bg-purple-600 text-white px-8 py-4 rounded-full text-base font-bold hover:bg-purple-500 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
                어떤 서비스인지 보기
              </a>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gradient-to-b from-[#1a0b2e] to-[#2d1b4e]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">50만+</div>
                <div className="text-sm text-gray-400">누적 구직자</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">3만+</div>
                <div className="text-sm text-gray-400">파트너 업체</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">92%</div>
                <div className="text-sm text-gray-400">취업 성공률</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2">4.8★</div>
                <div className="text-sm text-gray-400">앱 평점</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-gradient-to-b from-[#2d1b4e] to-[#1a0b2e]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-4 py-1 text-sm mb-4">
                왜 온니잡인가
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                퀸들이 선택한<br />이유가 있습니다
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                단순한 공고 나열이 아닙니다. 야간 업종 특화 매칭부터 빠른 채팅 연결까지, 온니잡은 모든 과정을 함께합니다.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: '🎯', title: '맞춤 업종 매칭', desc: '희망 조건, 지역, 업종을 설정하면 딱 맞는 공고를 우선 추천해드립니다.' },
                { icon: '💬', title: '업체와 바로 채팅', desc: '마음에 드는 공고에 바로 채팅 연결. 번거로운 전화 없이 빠르게 확인하세요.' },
                { icon: '⚡', title: '원클릭 지원', desc: '프로필 한 번 작성으로 원하는 곳에 바로 지원 완료. 시간을 아끼세요.' },
                { icon: '🔔', title: '실시간 알림', desc: '새 공고, 연락 온 업체, 지원 결과까지 놓치지 않게 실시간으로 알려드립니다.' },
                { icon: '🏢', title: '업체 채용 관리', desc: '업체 담당자라면 지원자 관리, 연락을 하나의 대시보드에서 깔끔하게 처리하세요.' },
                { icon: '🔒', title: '안전한 구직 환경', desc: '검증된 업체 정보와 투명한 공고로 안심하고 지원할 수 있습니다.' }
              ].map((feature, idx) => (
                <div key={idx} className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8 hover:border-purple-400/50 transition-all hover:scale-105">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 bg-[#1a0b2e]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-4 py-1 text-sm mb-4">
                사용 방법
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">
                3분이면<br />충분합니다 👑
              </h2>
            </div>

            <div className="space-y-8">
              {[
                { num: '1', title: '무료 회원가입', desc: '카카오, 네이버 계정으로 30초면 완료. 별도 비용 없이 모든 기능을 이용하세요.' },
                { num: '2', title: '프로필 작성', desc: '희망 업종, 지역, 조건을 입력하세요. 업체라면 채용 공고를 바로 등록할 수 있습니다.' },
                { num: '3', title: '맞춤 공고 확인', desc: '내 조건에 맞는 공고를 확인하고 관심 업체에 바로 지원 또는 채팅하세요.' },
                { num: '4', title: '퀸처럼 일 시작!', desc: '연락부터 최종 확정까지 온니잡 하나로 깔끔하게 마무리하세요.' }
              ].map((step, idx) => (
                <div key={idx} className="flex items-start gap-6 bg-gradient-to-r from-purple-900/30 to-transparent border-l-4 border-purple-500 rounded-lg p-6 hover:from-purple-900/50 transition-all">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full flex items-center justify-center text-2xl font-bold">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section id="reviews" className="py-24 bg-gradient-to-b from-[#2d1b4e] to-[#1a0b2e]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-4 py-1 text-sm mb-4">
                실제 후기
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">
                퀸알바들의<br />생생 후기 👑
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { stars: 5, text: '다른 앱은 공고가 너무 오래됐거나 연락이 안 됐는데, 온니잡은 업체가 바로 채팅으로 연락 와서 너무 편했어요!', name: '김민지 씨', role: '룸살롱 알바 성공', initial: '김' },
                { stars: 5, text: '공고 올리고 첫날부터 지원자가 왔어요. 지역 필터가 잘 돼 있어서 근처 구직자만 쏙쏙 연결돼서 좋았습니다.', name: '이사장 사장님', role: '노래방 업체 담당자', initial: '이' },
                { stars: 4, text: '조건 설정해두면 맞는 공고만 뜨니까 시간 낭비 없이 원하는 곳만 골라볼 수 있어서 너무 좋아요.', name: '박서연 씨', role: '호빠 알바 구직 중', initial: '박' }
              ].map((review, idx) => (
                <div key={idx} className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8 hover:border-purple-400/50 transition-all">
                  <div className="text-yellow-400 text-xl mb-4">
                    {'★'.repeat(review.stars)}{'☆'.repeat(5 - review.stars)}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">{review.text}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full flex items-center justify-center text-lg font-bold">
                      {review.initial}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{review.name}</div>
                      <div className="text-xs text-gray-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-24 bg-[#1a0b2e]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-4 py-1 text-sm mb-4">
                경쟁 비교
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">
                온니잡이<br />다른 이유
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-900/50 to-purple-800/30">
                    <th className="text-left p-4 text-sm font-semibold border border-purple-500/30">기능</th>
                    <th className="text-center p-4 text-sm font-semibold border border-purple-500/30 bg-purple-600/30">온니잡</th>
                    <th className="text-center p-4 text-sm font-semibold border border-purple-500/30">타 플랫폼 A</th>
                    <th className="text-center p-4 text-sm font-semibold border border-purple-500/30">타 플랫폼 B</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: '야간 업종 특화 공고', onnyjob: true, a: false, b: false },
                    { feature: '업체-구직자 채팅', onnyjob: true, a: false, b: false },
                    { feature: '원클릭 지원', onnyjob: true, a: true, b: false },
                    { feature: '호빠/호스트바 무료 광고', onnyjob: true, a: false, b: false },
                    { feature: '무료 이용', onnyjob: true, a: true, b: '부분 유료' },
                    { feature: '모바일 앱 지원', onnyjob: true, a: true, b: true }
                  ].map((row, idx) => (
                    <tr key={idx} className="hover:bg-purple-900/20 transition-colors">
                      <td className="p-4 text-sm border border-purple-500/30">{row.feature}</td>
                      <td className="p-4 text-center border border-purple-500/30 bg-purple-600/10">
                        {typeof row.onnyjob === 'boolean' ? (
                          row.onnyjob ? <span className="text-green-400 text-xl">✔</span> : <span className="text-red-400 text-xl">✘</span>
                        ) : (
                          <span className="text-sm">{row.onnyjob}</span>
                        )}
                      </td>
                      <td className="p-4 text-center border border-purple-500/30">
                        {typeof row.a === 'boolean' ? (
                          row.a ? <span className="text-green-400 text-xl">✔</span> : <span className="text-red-400 text-xl">✘</span>
                        ) : (
                          <span className="text-sm">{row.a}</span>
                        )}
                      </td>
                      <td className="p-4 text-center border border-purple-500/30">
                        {typeof row.b === 'boolean' ? (
                          row.b ? <span className="text-green-400 text-xl">✔</span> : <span className="text-red-400 text-xl">✘</span>
                        ) : (
                          <span className="text-sm text-gray-400">{row.b}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Industry Section */}
        <section id="industry" className="py-24 bg-gradient-to-b from-[#2d1b4e] to-[#1a0b2e]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block bg-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-full px-4 py-1 text-sm mb-4">
                업종별 구인광고
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                우리 업종도<br />온니잡에서 👑
              </h2>
              <p className="text-gray-400">
                룸살롱부터 노래방, 호빠까지 — 온니잡에서 원하는 인재를 만나보세요.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { badge: 'PREMIUM', badgeColor: 'bg-yellow-500', icon: '🍾', title: '룸살롱 · 클럽', desc: '홀서빙, 바텐더, 매니저 등 전문 인력을 온니잡에서 빠르게 채용하세요.', type: '유료 광고', btnText: '광고 문의하기', btnColor: 'bg-purple-600 hover:bg-purple-500' },
                { badge: 'PREMIUM', badgeColor: 'bg-yellow-500', icon: '🎤', title: '노래방 · 단란주점', desc: '카운터, 도우미, 홀 서빙 등 노래방·단란주점 운영 인력을 지역 기반으로 매칭하세요.', type: '유료 광고', btnText: '광고 문의하기', btnColor: 'bg-purple-600 hover:bg-purple-500' },
                { badge: '🎁 무료', badgeColor: 'bg-green-500', icon: '🥂', title: '호빠 · 호스트바', desc: '호스트바·호빠 구인 광고는 온니잡에서 완전 무료! 지금 바로 공고를 등록하고 원하는 인재를 만나보세요.', type: '✔ 무료 광고 가능', btnText: '지금 무료 등록', btnColor: 'bg-yellow-400 hover:bg-yellow-300 text-black' }
              ].map((industry, idx) => (
                <div key={idx} className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl overflow-hidden hover:border-purple-400/50 transition-all hover:scale-105">
                  <div className="p-8">
                    <div className={`inline-block ${industry.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full mb-4`}>
                      {industry.badge}
                    </div>
                    <div className="text-5xl mb-4">{industry.icon}</div>
                    <h3 className="text-xl font-bold mb-3">{industry.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-6">{industry.desc}</p>
                  </div>
                  <div className="px-8 pb-8">
                    <div className="text-xs text-gray-500 mb-3">{industry.type}</div>
                    <a href="https://onnyjob.kr" target="_blank" rel="noopener noreferrer" className={`block text-center ${industry.btnColor} px-6 py-3 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer`}>
                      {industry.btnText}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Bottom */}
        <section className="py-24 bg-gradient-to-b from-[#1a0b2e] to-[#0d0520]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              지금, <span className="text-yellow-400">퀸처럼</span> 시작하세요
            </h2>
            <p className="text-gray-400 mb-8">
              무료 가입 후 내게 맞는 공고를 바로 확인해보세요.<br />
              퀸알바의 선택, 온니잡이 함께합니다. 👑
            </p>
            <a href="https://onnyjob.kr" target="_blank" rel="noopener noreferrer" className="inline-block bg-yellow-400 text-black px-10 py-4 rounded-full text-base font-bold hover:bg-yellow-300 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              👑 온니잡 무료 시작하기
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#0d0520] py-8 border-t border-purple-900/30">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm text-gray-500">
              © 2025 <span className="text-purple-400 font-semibold">온니잡</span> — 퀸알바의 선택. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}